import {
  createSuperAdmin,
  endImpersonation,
  cancelInvite,
  setUserPassword,
  startImpersonation,
  updateSuperAdmin,
  updateUser,
} from "@/api/user-management/userManagement.mutations";
import type {
  SetUserPasswordPayload,
  StartImpersonationPayload,
  UpdateSuperAdminPayload,
  UpdateUserPayload,
} from "@/api/user-management/userManagement.mutations.types";
import { ACCESS_QUERY_ROOT } from "@/api/access/access.queries";
import {
  createUserWithAccess,
  saveFollowUps,
  type FollowUpFailures,
} from "@/api/user-management/createUserWithAccess";
import { PermissionsQueryKeys } from "@/api/roles-permissions/roles-permissions.queries";
import { UserManagementQueryKeys } from "@/api/user-management/userManagement.queries";
import { refreshPersonRecord } from "@/hooks/user-management/personRecordQueryKeys";
import { refreshUserHistory, UserHistoryQueryKeys } from "@/api/user-management/userHistory";
import { useImpersonationStore } from "@/store/useImpersonationStore";
import { SERVENGINE_SCOPE, useEntityTenantStore } from "@/store/entityTenantStore";
import { type QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { handleApiError } from "@/utils/responseHandler";
import type { ApiError, CustomAxiosRequestConfig } from "@/types/common.types";

/** A user write can change who holds a role: the roles list's count, the record's, and its holders. */
const ROLE_HOLDING_QUERY_KEYS = [
  [PermissionsQueryKeys.getAllRoles],
  [PermissionsQueryKeys.getRoleById],
  [PermissionsQueryKeys.getRoleHolders],
] as const;

/**
 * A user write also writes rows to the person's History, which the Details tab shows beside the
 * edit.
 */
export const invalidateRoleHoldings = (queryClient: QueryClient) => {
  for (const queryKey of ROLE_HOLDING_QUERY_KEYS) {
    queryClient.invalidateQueries({ queryKey });
  }
  refreshUserHistory(queryClient, [UserHistoryQueryKeys.history]);
};

/**
 * New User's save: the person, then their temporary roles and scope. Resolves with what did not
 * land rather than throwing once the person exists, so its failures are the page's to show.
 */
export const useCreateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createUserWithAccess,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [UserManagementQueryKeys.roles],
      });
      invalidateRoleHoldings(queryClient);
    },
  });
};

/** Try Again after a partial save: only the pieces that did not land, for a person who exists. */
export const useRetryNewUserFollowUps = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, failures }: { userId: string; failures: FollowUpFailures }) =>
      saveFollowUps(userId, failures.temporaryRoles, failures.entities),
    onSuccess: () => invalidateRoleHoldings(queryClient),
  });
};

export const useCreateSuperAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // Quiet, so a taken email (409) is said once, on the Email field, by the page.
    mutationFn: (payload: Parameters<typeof createSuperAdmin>[0]) =>
      createSuperAdmin(payload, { suppressErrorAlert: true } as CustomAxiosRequestConfig),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [UserManagementQueryKeys.roles],
      });
      invalidateRoleHoldings(queryClient);
    },
    onError: (error) => {
      handleApiError(error as ApiError);
    },
  });
};

export const useUpdateSuperAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, payload }: { userId: string; payload: UpdateSuperAdminPayload }) =>
      updateSuperAdmin(userId, payload),
    onSuccess: (_data, { userId }) => refreshPersonRecord(queryClient, userId, "profile"),
    onError: (error) => {
      handleApiError(error as ApiError);
    },
  });
};

// The Edit User page saves the person's details, roles and entities in one request.
export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, payload }: { userId: string; payload: UpdateUserPayload }) =>
      updateUser(userId, payload),
    onSuccess: (_data, { userId }) =>
      refreshPersonRecord(queryClient, userId, ["roles", "profile"]),
    onError: (error) => {
      handleApiError(error as ApiError);
    },
  });
};

// No query carries password data, but setting one writes a row to the person's History. Errors
// are handled by the caller: the modal shows INVALID_PASSWORD inline instead of as a toast.
export const useSetUserPassword = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      payload,
      actingEntityId,
    }: {
      userId: string;
      payload: SetUserPasswordPayload;
      actingEntityId?: string;
    }) => setUserPassword(userId, payload, actingEntityId),
    onSuccess: (_data, { userId }) => refreshPersonRecord(queryClient, userId, "password"),
  });
};

// The users list's query sits under `roles`.
const USERS_LIST_QUERY_KEYS = [
  [UserManagementQueryKeys.roles],
  [UserManagementQueryKeys.userDetail],
] as const;

export const useCancelInvite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, entityId }: { userId: string; entityId: string }) =>
      cancelInvite(userId, entityId),
    // Everywhere the cancelled person still shows: the list and their record, the people pickers,
    // their own roles, scope and effective access, and the holders of every role they held there.
    onSuccess: (_data, { userId }) => {
      refreshPersonRecord(queryClient, userId, ["roles", "profile"]);
      invalidateRoleHoldings(queryClient);
    },
    // The person has signed in since the list was read, so the row should stop offering the cancel.
    onError: (error) => {
      if ((error as ApiError)?.response?.data?.error?.code !== "INVITE_ACCEPTED") return;
      for (const queryKey of USERS_LIST_QUERY_KEYS) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
};

export const IMPERSONATION_MUTATION_KEY = ["startImpersonation"] as const;

// The access key carries no user (contract 7.4), and the route guard reads it with
// ensureQueryData, which serves a cached answer however stale; so the previous person's answer is
// dropped, not marked stale, before the next guarded route.
const forgetEffectiveAccess = (queryClient: QueryClient) =>
  queryClient.removeQueries({ queryKey: [ACCESS_QUERY_ROOT] });

export const useStartImpersonation = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { startImpersonation: storeStartImpersonation } = useImpersonationStore();
  return useMutation({
    mutationKey: IMPERSONATION_MUTATION_KEY,
    mutationFn: (payload: StartImpersonationPayload) => startImpersonation(payload),
    onSuccess: async (data) => {
      storeStartImpersonation(data.data);
      forgetEffectiveAccess(queryClient);
      navigate({ to: "/" });
    },
    onError: (error) => {
      handleApiError(error as ApiError);
    },
  });
};

export const useEndImpersonation = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: IMPERSONATION_MUTATION_KEY,
    mutationFn: endImpersonation,
    onSuccess: async () => {
      useImpersonationStore.getState().resetImpersonationStore();
      // An entity picked while impersonating would otherwise ride along as the
      // admin's own acting entity on the very next request.
      useEntityTenantStore.getState().setEntityTenant(SERVENGINE_SCOPE);
      forgetEffectiveAccess(queryClient);
      navigate({ to: "/user-management" });
    },
    onError: (error) => {
      handleApiError(error as ApiError);
    },
  });
};
